/**
 * useLatestAlerts — the handful of still-open alerts a DASHBOARD shows at its top, and how
 * many there are in total.
 *
 * The alert board's `useAlertBoard` already owns the same feed, but it owns far more with it:
 * the camera roster, per-camera counters, live WebRTC tiles, preview countdowns, an
 * infinite-scroll card list. A dashboard wants none of that — it wants the newest two or three
 * alerts and a number — so this is the thin half of that hook rather than a second caller of
 * the whole one, and it opens exactly one subscription to do it.
 *
 * Two sources, because they answer two different questions:
 *   • `api.watch()` gives the LIST — the active feed on connect, then every arrival, preview
 *     and lifecycle change as it happens. That feed is capped by the node (100 rows), which is
 *     plenty for a strip showing two cards but useless as a count on a busy machine.
 *   • `api.listActiveAlerts({limit: 1})` gives the COUNT — the node's own total, which is what
 *     the "N open" pill must read. It is fetched on mount and after a wipe, moved by the same
 *     live deltas that move the list, and re-synced whenever the page calls `refresh()` (the
 *     dashboard's existing poll does), so it can't drift far from what the board would show.
 *
 * Acknowledge/resolve are optimistic: the alert leaves the strip immediately and the request
 * follows. The node echoes an `alert:lifecycle` for the same alert moments later, which the
 * removal below is idempotent against — an alert cleared twice decrements the count once.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HexAlert, HexAlertsApi } from '../api/contracts';
import { isActiveAlert } from './useAlertBoard';

/** How much of the live feed to hold. The strip renders 2–3; the rest is headroom so that
 *  acknowledging the top cards reveals the next ones without a refetch. */
const FEED_CAP = 20;

/** Alert timestamps arrive as epoch seconds from the node and ms from some callers. */
const ms = (ts?: number): number => (!ts ? 0 : ts < 1e12 ? ts * 1000 : ts);

/** Newest first. Ties keep insertion order, so a live arrival stays on top of an older row
 *  stamped in the same second. */
const byNewest = (a: HexAlert, b: HexAlert) => ms(b.timestamp) - ms(a.timestamp);

export interface UseLatestAlertsOptions {
  api: HexAlertsApi;
  /** Cloud: no node selected yet — hold the subscription and the count query. */
  blocked?: boolean;
  /** Changes when the selected node changes → re-subscribe and re-count. */
  nodeKey?: string;
}

export interface LatestAlerts {
  /** Active alerts, newest first (capped — see FEED_CAP). */
  alerts: HexAlert[];
  /** How many alerts are open on the node in total, which is ≥ `alerts.length`. */
  open: number;
  /** True until the first feed lands, so the strip can show placeholders rather than
   *  "no open alerts" — the two must never look the same. */
  loading: boolean;
  acknowledge: (alert: HexAlert) => void;
  resolve: (alert: HexAlert) => void;
  /** Whether the node implements each action at all (the buttons hide when it doesn't). */
  canAcknowledge: boolean;
  canResolve: boolean;
  /** Re-read the open count from the node. The list needs no refresh — it is live. */
  refresh: () => void;
  /**
   * Bumped on every LIVE change to the feed: an arrival, a lifecycle clear, a node-side wipe.
   *
   * For the figures that sit near this strip and are NOT live. The dashboard's alert counts come
   * from a 30-second poll of an aggregate endpoint, so a socket alert appeared in the strip
   * instantly and stayed uncounted by the card beside it for most of a minute — which reads as
   * the card being broken, not as two different refresh strategies. A page can watch this and
   * re-read whatever it polls when the feed actually moves, instead of waiting for its timer.
   *
   * Deliberately a counter and not the alert: consumers should re-ask their own source rather
   * than try to patch a total they didn't compute.
   */
  revision: number;
}

export function useLatestAlerts({ api, blocked = false, nodeKey }: UseLatestAlertsOptions): LatestAlerts {
  const [alerts, setAlerts] = useState<HexAlert[]>([]);
  const [loading, setLoading] = useState(true);
  /** The node's own total, or null when it can't be asked — then the feed length stands in. */
  const [total, setTotal] = useState<number | null>(null);
  /** Alerts already taken off the strip (optimistically or by a lifecycle event), so the
   *  echoed event for the same alert doesn't decrement the count a second time. */
  const clearedIds = useRef<Set<string>>(new Set());
  /** See `revision` on the returned shape. */
  const [revision, setRevision] = useState(0);
  const bump = useCallback(() => setRevision((r) => r + 1), []);

  const refresh = useCallback(() => {
    if (blocked || !api.listActiveAlerts) return;
    api
      .listActiveAlerts({ page: 1, limit: 1 })
      .then((p) => setTotal(Number(p?.total) || 0))
      // Leave the previous count rather than dropping to the feed length: a failed count
      // query is not evidence that the node is quieter than it was.
      .catch(() => {});
  }, [api, blocked]);

  /** Take one alert off the strip, at most once. */
  const clear = useCallback((alertId?: string, cameraId?: string) => {
    const key = alertId || cameraId;
    if (!key) return;
    if (clearedIds.current.has(key)) return;
    clearedIds.current.add(key);
    setAlerts((prev) => prev.filter((a) => (alertId ? a.id !== alertId : a.cameraId !== cameraId)));
    setTotal((t) => (t == null ? t : Math.max(0, t - 1)));
    bump();
  }, [bump]);

  useEffect(() => {
    if (blocked) {
      setAlerts([]);
      setTotal(null);
      setLoading(false);
      return;
    }
    setAlerts([]);
    setLoading(true);
    clearedIds.current = new Set();
    refresh();

    const stop = api.watch({
      onSnapshot: (_counters, recent) => {
        // Authoritative: whatever the node says is open right now, minus anything this
        // session has already actioned but whose echo hasn't arrived yet.
        setAlerts(
          recent
            .filter((a) => isActiveAlert(a) && !(a.id && clearedIds.current.has(a.id)))
            .sort(byNewest)
            .slice(0, FEED_CAP),
        );
        setLoading(false);
        // Pollers re-snapshot on a timer; a node that can't be counted rides on the feed.
        if (!api.listActiveAlerts) setTotal(recent.filter(isActiveAlert).length);
      },
      onAlert: (a) => {
        if (!isActiveAlert(a)) return;
        // An alert that arrives again after being actioned is a genuinely new row on the
        // same camera — stop suppressing it.
        if (a.id) clearedIds.current.delete(a.id);
        setAlerts((prev) => {
          if (a.id && prev.some((p) => p.id === a.id)) return prev;
          return [a, ...prev].sort(byNewest).slice(0, FEED_CAP);
        });
        setTotal((t) => (t == null ? t : t + 1));
        setLoading(false);
        bump();
      },
      onAlertPreview: (p) => {
        if (!p.alertId) return;
        // The alert is already on screen and only its image is new. Bumping `previewReady`
        // is what re-runs a lazy preview resolver that would have been too early to succeed.
        setAlerts((prev) =>
          prev.map((a) =>
            a.id === p.alertId
              ? { ...a, snapshotUrl: p.snapshotUrl ?? a.snapshotUrl, previewReady: (a.previewReady || 0) + 1 }
              : a,
          ),
        );
      },
      onLifecycle: (c) => {
        // Any non-'new' transition (ack/assign/resolve/cancel) takes the alert off the strip.
        if (c.status && !isActiveAlert({ cameraId: c.cameraId, severity: c.severity || 'normal', status: c.status })) {
          clear(c.alertId, c.cameraId);
        }
      },
      onCleared: () => {
        // The node truncated its event store: every row held here is gone, and no per-alert
        // event will say so. Drop the lot and re-read the count; the adapter follows this
        // with a fresh snapshot for whatever is genuinely still open.
        setAlerts([]);
        setTotal(0);
        clearedIds.current = new Set();
        refresh();
        bump();
      },
    });
    return stop;
  }, [api, nodeKey, blocked, clear, refresh, bump]);

  const acknowledge = useCallback(
    (alert: HexAlert) => {
      if (!api.acknowledgeAlert || !alert.id) return;
      clear(alert.id);
      api.acknowledgeAlert(alert.id).catch(() => {});
    },
    [api, clear],
  );

  const resolve = useCallback(
    (alert: HexAlert) => {
      if (!api.resolveAlert || !alert.id) return;
      clear(alert.id);
      api.resolveAlert(alert.id).catch(() => {});
    },
    [api, clear],
  );

  const open = useMemo(() => Math.max(total ?? 0, alerts.length), [total, alerts.length]);

  return {
    alerts,
    open,
    loading,
    acknowledge,
    resolve,
    canAcknowledge: !!api.acknowledgeAlert,
    canResolve: !!api.resolveAlert,
    refresh,
    revision,
  };
}
